import { useRef, useMemo, useState, useCallback } from 'react';
import { standardizeLocale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { ISSUE_NAVIGATOR_ACTION_MODULE } from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import type { IssueNavigatorAction } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import { fetchModules } from '@atlassian/jira-forge-ui-utils/src/utils/fetch-modules/index.tsx';
import { isFilterId } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import { useActiveJql } from '@atlassian/jira-issue-navigator/src/services/active-jql/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import type { IssueNavigatorPluggableItemsQuery_jiraWebSection_webItems_nodes as ConnectPluggableItemType } from './__generated_apollo__/IssueNavigatorPluggableItemsQuery';
import { ISSUE_NAVIGATOR_PLUGGABLE_ITEMS_QUERY } from './gql.tsx';

export type PluggableItemsResponse = {
	data: PluggableItem[] | null | undefined;
	loading: boolean;
	error: boolean;
};

type PluggableItemsFetcher = (forceRefetch?: boolean) => void;

export const usePluggableItemsQuery = (): [PluggableItemsFetcher, PluggableItemsResponse] => {
	const [pluggableItems, setPluggableItems] = useState<PluggableItemsResponse>({
		...pluggableItemsInitialState,
	});

	const { jql, filterId } = useActiveJql();
	const cloudId = useCloudId();
	const isAnonymous = useIsAnonymous();
	const locale = useLocale();

	const key = !jql && isFilterId(filterId) ? filterId : jql;

	const currentKeyRef = useRef(key);
	const { current: intlCollator } = useRef(new Intl.Collator(standardizeLocale(locale)));
	const lastKeyRef = useRef<unknown>(null);

	const fetchPluggableItems: PluggableItemsFetcher = useCallback(
		(forceRefetch = false) => {
			if (
				forceRefetch ||
				lastKeyRef.current == null ||
				lastKeyRef.current !== currentKeyRef.current
			) {
				setPluggableItems({
					data: undefined,
					loading: true,
					error: false,
				});

				Promise.all([
					fetchModules({
						cloudId,
						isAnonymous,
						context: {},
						types: [ISSUE_NAVIGATOR_ACTION_MODULE],
						includeHidden: false,
						source: 'issue-navigator',
					}),
					performPostRequest('/rest/gira/1/?operation=IssueNavigatorPluggableItemsQuery', {
						body: JSON.stringify({
							query: ISSUE_NAVIGATOR_PLUGGABLE_ITEMS_QUERY,
							variables: { first: 100 },
						}),
					}),
				])
					.then(([forgeModules, connectResponse]) => {
						if (connectResponse.errors) {
							setPluggableItems({
								data: null,
								loading: false,
								error: true,
							});
						} else {
							setPluggableItems({
								data: transform(
									forgeModules[ISSUE_NAVIGATOR_ACTION_MODULE],
									connectResponse.data?.jiraWebSection?.webItems?.nodes,
									intlCollator.compare,
								),
								loading: false,
								error: false,
							});
						}
					})
					.catch(() => {
						setPluggableItems({
							data: null,
							loading: false,
							error: true,
						});
					});

				lastKeyRef.current = currentKeyRef.current;
			}
		},
		[cloudId, isAnonymous, intlCollator.compare],
	);

	// Sync this to avoid recreating the callback and mitigating rerender
	currentKeyRef.current = key;

	return useMemo(
		() => [fetchPluggableItems, pluggableItems],
		[pluggableItems, fetchPluggableItems],
	);
};

type PluggableEntity<Type, Metadata> = {
	type: Type;
	id: string;
	title: string;
	iconUrl: string | null;
	tooltip: string | null;
	metadata: Metadata;
};

export type ForgePluggableEntity = PluggableEntity<'forge', IssueNavigatorAction>;
export type ConnectPluggableEntity = PluggableEntity<'connect', ConnectPluggableItemType>;

export type PluggableItem = ForgePluggableEntity | ConnectPluggableEntity;

export const pluggableItemsInitialState: PluggableItemsResponse = {
	data: undefined,
	loading: false,
	error: false,
};

function transform(
	forgeModules: IssueNavigatorAction[],
	connectModules: ConnectPluggableItemType[],
	comparator: (a: string, b: string) => number,
): PluggableItem[] {
	return [
		...forgeModules.map((module) => {
			return {
				type: 'forge' as const,
				id: module.id,
				title: module.properties.title,
				iconUrl: module.properties.icon ?? null,
				tooltip: module.properties.tooltip ?? null,
				metadata: module,
			};
		}),
		...connectModules
			.filter((item) => item.label != null)
			.map((module) => ({
				type: 'connect' as const,
				id: module.id,
				title: module.label || '',
				iconUrl: module.iconUrl,
				tooltip: module.tooltip,
				metadata: module,
			})),
	].sort((a, b) => comparator(a.title, b.title));
}
